import React from "react";
import type { FallbackProps } from "react-error-boundary";
import { ErrorBoundary } from "react-error-boundary";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { debugLog } from "@/utils/debugLog";
import {
  consumeForeignErrorRetry,
  isForeignScriptError,
  repairBrokenAutomationBindings,
} from "@/utils/foreignScriptError";

/**
 * Sentinel passed to resetErrorBoundary() for foreign-script auto-resets so
 * onReset can tell them apart from app-error resets: a foreign (extension /
 * automation-shim) error says nothing about our own state, so it must not
 * clear the user's selected organization.
 */
const FOREIGN_RESET_REASON = "foreign-script-error";

function ContextErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  process.env.NODE_ENV === "development" &&
    console.error("Context Error Boundary caught error:", error);

  // Don't show error boundary for common loading states or expected errors
  const isExpectedError =
    error.message?.includes("useAuth must be used within") ||
    error.message?.includes("loading") ||
    error.message?.includes("No user profile");

  // Errors thrown by scripts that are provably not ours — browser extensions
  // or automation-browser binding shims (BetterWright/CloakBrowser inject
  // Playwright-style bindings that can throw inside React's commit phase) —
  // must never replace the whole app with the fatal card. Auto-reset instead,
  // bounded by a retry budget so a persistently broken injection still
  // surfaces the card rather than looping forever. Consumption is idempotent
  // per error object, so fallback re-renders don't burn extra budget.
  const isForeignError = isForeignScriptError(error) && consumeForeignErrorRetry(error);
  const shouldAutoReset = isExpectedError || isForeignError;

  // Auto-reset for expected/foreign errors. The repair, the log and the timer
  // all live in an effect with cleanup, not in render: they are side effects,
  // and an unmount before the timer fires would otherwise setState on a dead
  // boundary.
  React.useEffect(() => {
    if (!shouldAutoReset) return;

    // Remove the cause before retrying: a broken automation binding throws on
    // EVERY commit, so without this repair the reset below would re-throw
    // until the retry budget ran out and the fatal card appeared anyway.
    if (isForeignError) repairBrokenAutomationBindings();
    debugLog(
      isForeignError
        ? "Foreign-script error caught by boundary, attempting reset:"
        : "Expected error caught by boundary, attempting reset:",
      error.message
    );

    const resetTimer = window.setTimeout(
      () => resetErrorBoundary(isForeignError ? FOREIGN_RESET_REASON : undefined),
      100
    );
    return () => window.clearTimeout(resetTimer);
  }, [error.message, isForeignError, resetErrorBoundary, shouldAutoReset]);

  if (shouldAutoReset) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Alert variant="destructive" className="max-w-md">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Application Error</AlertTitle>
        <AlertDescription>
          <p className="mb-3">
            There was an error loading the application. This might be a temporary issue.
          </p>
          <p className="mb-3 text-sm text-muted-foreground">Error: {error.message}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={resetErrorBoundary}>
              Try Again
            </Button>
            <Button variant="default" onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}

interface ContextErrorBoundaryProps {
  children: React.ReactNode;
}

export function ContextErrorBoundary({ children }: ContextErrorBoundaryProps) {
  return (
    <ErrorBoundary
      FallbackComponent={ContextErrorFallback}
      onReset={(details) => {
        const isForeignReset =
          details.reason === "imperative-api" && details.args[0] === FOREIGN_RESET_REASON;
        // Clear any cached data that might be causing issues — but only for
        // our own errors; a foreign-script reset must not touch org state.
        if (!isForeignReset) {
          localStorage.removeItem("currentOrganizationId");
        }
        process.env.NODE_ENV === "development" && console.log("Context error boundary reset");
      }}
      onError={(error, errorInfo) => {
        process.env.NODE_ENV === "development" &&
          console.error("Context Error Boundary Error:", error);
        process.env.NODE_ENV === "development" && console.error("Error Info:", errorInfo);
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
